import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import useConfirm from 'src/hooks/useConfirm';
import useInvoiceScanManager from 'src/hooks/useInvoiceScanManager';
import useDeleteScanManagerInvoicesApi from 'src/services/useDeleteScanManagerInvoices';

const AttachmentDelete = () => {
  const { refreshPage, setRefreshCompany, selectedDocuments, attachmentListData } =
    useInvoiceScanManager();

  const { deletePendingInvoiceAttachments, isDeleteInvoiceAttachmentsLoading } =
    useDeleteScanManagerInvoicesApi();
  const { confirm } = useConfirm();
  const hasSelectedDocuments = selectedDocuments.length > 0;
  const hasDeletedDocuments = selectedDocuments.some((doc) => doc.isDeleted);
  const hasAttachedDocuments = selectedDocuments.every((doc) => doc.receiptId === null);

  const { informationMessageList, errorMessageList, isDeleteButtonDisabled } = useMemo(() => {
    const isButtonDisabled = !hasSelectedDocuments || !hasAttachedDocuments || hasDeletedDocuments;
    const informationMessageList: string[] = [];
    const errorMessageList: string[] = [];

    if (attachmentListData.length === 0) {
      informationMessageList.push('You have no record(s) in queue to Delete.');
    }
    if (!hasSelectedDocuments) {
      informationMessageList.push('Please select record(s) to Delete.');
    }
    if (hasDeletedDocuments) {
      errorMessageList.push('Please select only Undeleted record(s) to Delete.');
    }
    if (!hasAttachedDocuments) {
      errorMessageList.push('Please select only Unattached record(s) to Delete.');
    }

    return {
      isDeleteButtonDisabled: isButtonDisabled,
      informationMessageList,
      errorMessageList,
    };
  }, [attachmentListData.length, hasAttachedDocuments, hasDeletedDocuments, hasSelectedDocuments]);

  const deleteItem = useCallback(async () => {
    const attachIds = selectedDocuments.map((row) => row.attachId);

    await deletePendingInvoiceAttachments({ attachIds }).then(() => {
      refreshPage();
    });
  }, [deletePendingInvoiceAttachments, refreshPage, selectedDocuments]);

  const handleDelete = useCallback(async () => {
    const isDeleteAccepted = await confirm({
      title: 'Delete Confirmation',
      content: <div>Are you sure you want to Delete the selected record(s)?</div>,
      confirmButtonText: 'OK',
    });
    if (isDeleteAccepted) {
      await deleteItem();
      toast.success('Record(s) Deleted Successfully!');
      setRefreshCompany?.((prev) => !prev);
    }
  }, [confirm, deleteItem, setRefreshCompany]);

  return (
    <PopUp
      content={
        <div className="m-1 max-w-[300px]">
          <PopUpContentWrapper type="Error" contents={errorMessageList} />
          <PopUpContentWrapper type="Information" contents={informationMessageList} />
        </div>
      }
      show={isDeleteButtonDisabled}
    >
      <Button
        color="error"
        onClick={handleDelete}
        disabled={isDeleteButtonDisabled}
        loading={isDeleteInvoiceAttachmentsLoading}
      >
        Delete
      </Button>
    </PopUp>
  );
};

export default AttachmentDelete;
